import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatCard } from '../components/StatCard';
import { Async } from '../components/Async';
import { IcCreditCard, IcPill, IcFile } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getAR, generateClientDraft, draftResultMessage, ApiError, type ArAging } from '../lib/api';
import { fmtCents0 } from '../lib/format';

const BUCKETS: { key: keyof ArAging; label: string; danger?: boolean }[] = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1–30d' },
  { key: 'd31_60', label: '31–60d' },
  { key: 'd61_90', label: '61–90d' },
  { key: 'd90', label: '90d+', danger: true },
];

function dueLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days > 0) return `${date} · ${days}d overdue`;
  return `${date} · in ${-days}d`;
}

export const ArPage = () => {
  const { can } = useAuth();
  const canRun = can('billing:run');
  const state = useFetch(getAR);
  const [drafting, setDrafting] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function generateDraft(clientId: string, name: string) {
    if (!window.confirm(`Generate a draft for ${name}? Any existing open drafts for this client are superseded (voided).`)) return;
    setError(null);
    setMsg(null);
    setDrafting(clientId);
    try {
      const r = await generateClientDraft(clientId, { supersede: true });
      setMsg(draftResultMessage(r));
      state.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate draft');
    } finally {
      setDrafting(null);
    }
  }

  return (
    <Page>
      <PageHeader
        title="Accounts Receivable"
        sub="Outstanding invoiced balances aged by due date, plus accrued (unbilled) coverage not yet invoiced."
      />

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>
          {error}
        </div>
      )}
      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 13 }}>
          {msg} <Link to="/invoices?status=draft">View drafts →</Link>
        </div>
      )}

      <Async state={state} isEmpty={() => false} emptyLabel="">
        {(data) => {
          const t = data.totals;
          const exposure = t.outstandingCents + t.accruedCents;
          const maxBucket = Math.max(1, ...BUCKETS.map((b) => t[b.key]));
          return (
            <>
              <div className="grid grid-3">
                <StatCard tone="staged" icon={IcFile} label="Outstanding (AR)" value={fmtCents0(t.outstandingCents)} sub={`${t.openCount} open invoices`} />
                <StatCard tone="in-transit" icon={IcPill} label="Unbilled (accrued)" value={fmtCents0(t.accruedCents)} sub="not yet invoiced" />
                <StatCard tone="in-progress" icon={IcCreditCard} label="Total exposure" value={fmtCents0(exposure)} sub="AR + unbilled" />
              </div>

              <div>
                <div className="section-title">Aging</div>
                <PCard style={{ padding: 16 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {BUCKETS.map((b) => {
                      const v = t[b.key];
                      return (
                        <div key={b.key} style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: 'var(--p-muted-fg)', marginBottom: 4 }}>{b.label}</div>
                          <div className="tabular" style={{ fontSize: 16, fontWeight: 600, color: b.danger && v > 0 ? 'var(--status-error-ink,#b91c1c)' : undefined }}>
                            {fmtCents0(v)}
                          </div>
                          <div style={{ height: 6, marginTop: 6, borderRadius: 3, background: 'var(--p-border)', overflow: 'hidden' }}>
                            <div style={{ width: `${(v / maxBucket) * 100}%`, height: '100%', background: b.danger ? 'var(--status-error-ink,#b91c1c)' : 'var(--p-primary,#2563eb)' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </PCard>
              </div>

              <div>
                <div className="section-title">By client</div>
                {data.clients.length === 0 ? (
                  <PCard style={{ padding: 16, color: 'var(--p-muted-fg)', fontSize: 13 }}>Nothing outstanding or accrued.</PCard>
                ) : (
                  <PCard>
                    <table className="p-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th style={{ textAlign: 'right' }}>Unbilled</th>
                          <th style={{ textAlign: 'right' }}>Current</th>
                          <th style={{ textAlign: 'right' }}>1–30</th>
                          <th style={{ textAlign: 'right' }}>31–60</th>
                          <th style={{ textAlign: 'right' }}>61–90</th>
                          <th style={{ textAlign: 'right' }}>90d+</th>
                          <th style={{ textAlign: 'right' }}>Outstanding</th>
                          <th>Oldest due</th>
                          {canRun && <th style={{ textAlign: 'right' }}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {data.clients.map((c) => (
                          <tr key={c.clientId}>
                            <td style={{ fontWeight: 500 }}>{c.name}</td>
                            <td className="tabular muted" style={{ textAlign: 'right' }}>{c.accruedCents > 0 ? fmtCents0(c.accruedCents) : '—'}</td>
                            <td className="tabular muted" style={{ textAlign: 'right' }}>{c.aging.current ? fmtCents0(c.aging.current) : '·'}</td>
                            <td className="tabular muted" style={{ textAlign: 'right' }}>{c.aging.d1_30 ? fmtCents0(c.aging.d1_30) : '·'}</td>
                            <td className="tabular muted" style={{ textAlign: 'right' }}>{c.aging.d31_60 ? fmtCents0(c.aging.d31_60) : '·'}</td>
                            <td className="tabular muted" style={{ textAlign: 'right' }}>{c.aging.d61_90 ? fmtCents0(c.aging.d61_90) : '·'}</td>
                            <td className="tabular" style={{ textAlign: 'right', color: c.aging.d90 > 0 ? 'var(--status-error-ink,#b91c1c)' : undefined, fontWeight: c.aging.d90 > 0 ? 600 : 400 }}>
                              {c.aging.d90 ? fmtCents0(c.aging.d90) : '·'}
                            </td>
                            <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{c.outstandingCents ? fmtCents0(c.outstandingCents) : '—'}</td>
                            <td className="muted" style={{ fontSize: 12 }}>{dueLabel(c.oldestDueAt)}</td>
                            {canRun && (
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button className="btn" disabled={drafting === c.clientId} onClick={() => generateDraft(c.clientId, c.name)}>
                                  {drafting === c.clientId ? 'Generating…' : 'Generate draft'}
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PCard>
                )}
              </div>
            </>
          );
        }}
      </Async>
    </Page>
  );
};
